import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  TrendingUp,
  Wallet,
  ArrowUpRight,
  ShieldCheck,
  ChevronRight,
  Zap,
} from "lucide-react";
import {
  getRecommendedTransferPartners,
  getValuationAnalytics,
} from "../../../services/Front/dashboardService";
import { decryptData } from "../../../utils/crypto";
import Utils from "../../../utils";
import { notify } from "../../../components/Common/Alerts";

const PortfolioSummary = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [cardList, setCardList] = useState("");

  useEffect(() => {
    getValuationAnalytics()
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        setCardList(decrypted);
      })
      .catch((err) => {
        notify.error(Utils.getErrorMessage(err));
      });
  }, []);

  useEffect(() => {
    if (cardList?.cards?.length > 0) {
      getPartners();
    }
  }, [cardList?.cards]);

  const getPartners = async () => {
    try {
      const response = await getRecommendedTransferPartners({
        card_ids: cardList?.cards?.map((c) => c.card_id),
      });
      const decrypted = decryptData(response.data.data);
      setPartners(decrypted || []);
    } catch (error) {
      notify.error(Utils.getErrorMessage(error));
    }
  };

  const strongestProgramData = (cardList?.cards || []).reduce((acc, card) => {
    const program = card.reward_program_name || "Unknown";
    const estimatedValue = Number(card.latest_max || 0);
    acc[program] = (acc[program] || 0) + estimatedValue;
    return acc;
  }, {});

  const strongestProgram = Object.entries(strongestProgramData || {}).sort(
    (a, b) => b[1] - a[1]
  )[0];

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#050A18] px-4 py-6 sm:p-6 md:p-8 transition-colors duration-300">
      <div className="max-w-5xl mx-auto space-y-6">
        
        <div className="relative rounded-3xl bg-gradient-to-br from-[#CAA036] via-[#B8860B] to-[#8B6508] p-6 sm:p-8 text-white overflow-hidden shadow-xl">
          <div className="absolute right-0 top-0 bottom-0 w-1/2 opacity-10 pointer-events-none hidden md:block">
            <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
              <path d="M0,100 C30,40 70,60 100,0 L100,100 Z" fill="white" />
            </svg>
          </div>
          
          <div className="relative z-10 space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 backdrop-blur-md">
              <Zap size={14} className="text-amber-200 fill-amber-200" />
              <span className="text-[10px] font-bold tracking-widest uppercase text-amber-100">Premium Access</span>
            </div>
            
            <div className="max-w-xl space-y-2">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                Know the real value <br className="hidden sm:inline" />of your points.
              </h1>
              <p className="text-sm font-light text-amber-50/80 max-w-sm leading-relaxed">
                Real-time market insights & smart redemption recommendations for maximum value.
              </p>
            </div>

            <div className="pt-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t border-white/10">
              <div className="flex flex-wrap gap-4 sm:gap-6">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-xl bg-white/10 border border-white/10">
                    <Wallet size={16} />
                  </div>
                  <div>
                    <p className="text-base font-bold tabular-nums">{cardList.total_points || 0}</p>
                    <p className="text-[10px] text-amber-100/60 font-medium">Total Points</p>
                  </div>
                </div>
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-xl bg-white/10 border border-white/10">
                    <TrendingUp size={16} />
                  </div>
                  <div>
                    <p className="text-base font-bold tabular-nums whitespace-nowrap">
                      ${cardList.total_estimated_min_value || 0} – ${cardList.total_estimated_max_value || 0}
                    </p>
                    <p className="text-[10px] text-amber-100/60 font-medium">Estimated Value</p>
                  </div>
                </div>
              </div>

              <button 
                onClick={() => navigate("/dashboard-screen")}
                className="w-full sm:w-auto px-5 h-11 inline-flex items-center justify-center gap-2 rounded-xl bg-white text-slate-900 text-xs font-bold tracking-wide hover:bg-slate-50 active:scale-95 transition-all shadow-md whitespace-nowrap"
              >
                Find Redemptions
                <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-[#0F172A] border border-slate-100 dark:border-slate-800 p-4 rounded-2xl shadow-sm flex items-start gap-3">
            <div className="p-2 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 text-emerald-500 mt-0.5">
              <ShieldCheck size={18} />
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Portfolio Status</p>
              <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 mt-0.5">Ready for Redemption</h3>
              <p className="text-[11px] text-slate-400 mt-1 leading-snug">Your portfolio is optimized for high value redemptions.</p>
            </div>
          </div>

          <div className="bg-white dark:bg-[#0F172A] border border-slate-100 dark:border-slate-800 p-4 rounded-2xl shadow-sm flex items-start gap-3">
            <div className="p-2 rounded-xl bg-blue-50 dark:bg-blue-950/30 text-blue-500 mt-0.5">
              <Wallet size={18} />
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Total Points</p>
              <h3 className="text-lg font-extrabold text-slate-900 dark:text-white mt-0.5 tabular-nums">
                {cardList.total_points?.toLocaleString() || 0}
              </h3>
              <p className="text-[11px] text-slate-400 mt-1">Across {cardList?.cards?.length || 0} Accounts</p>
            </div>
          </div>

          <div className="bg-white dark:bg-[#0F172A] border border-slate-100 dark:border-slate-800 p-4 rounded-2xl shadow-sm flex items-start gap-3">
            <div className="p-2 rounded-xl bg-cyan-50 dark:bg-cyan-950/30 text-cyan-500 mt-0.5">
              <TrendingUp size={18} />
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Estimated Value</p>
              <h3 className="text-base font-extrabold text-slate-900 dark:text-white mt-0.5 tabular-nums whitespace-nowrap">
                ${cardList.total_estimated_min_value} – ${cardList.total_estimated_max_value}
              </h3>
              <p className="text-[11px] text-slate-400 mt-1">Based on current market</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-[#0F172A] border border-slate-100 dark:border-slate-800 p-5 sm:p-6 rounded-2xl shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TrendingUp size={16} className="text-amber-500" />
              <h2 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-slate-800 dark:text-white">Recommended Partners</h2>
            </div>
            <button className="text-[11px] font-semibold text-amber-600 dark:text-amber-400 hover:underline">View all</button>
          </div>

          {partners?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {partners.map((partner) => (
                <div key={partner.id} className="p-4 rounded-xl bg-slate-50 dark:bg-[#050A18] border border-slate-100 dark:border-slate-800/60 flex flex-col justify-between gap-4">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-white dark:bg-[#0F172A] border border-slate-100 dark:border-slate-800 flex items-center justify-center font-bold text-amber-500">
                        <ArrowUpRight size={16} />
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-slate-900 dark:text-white leading-tight">{partner.partner_name}</h4>
                        <p className="text-[10px] text-slate-400 mt-0.5">From {partner.source_program}</p>
                      </div>
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${
                      Number(partner.avg_cpp) >= 2 ? "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600" : "bg-amber-50 dark:bg-amber-950/30 text-amber-600"
                    }`}>
                      {Number(partner.avg_cpp) >= 2 ? "EXCELLENT" : "GOOD"}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-200/60 dark:border-slate-800">
                    <div>
                      <p className="text-[10px] uppercase font-semibold text-slate-400 tracking-wider">Transfer Ratio</p>
                      <p className="text-sm font-bold text-slate-800 dark:text-white mt-0.5">{Number(partner.transfer_ratio)}:1</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] uppercase font-semibold text-slate-400 tracking-wider">Avg CPP</p>
                      <p className="text-sm font-bold text-amber-500 mt-0.5">{Number(partner.avg_cpp)}</p>
                    </div>
                  </div>

                  {Number(partner.bonus_percentage) > 0 && (
                    <div className="inline-flex self-start rounded-md bg-amber-500/10 border border-amber-500/20 px-2 py-0.5">
                      <p className="text-[10px] text-amber-600 dark:text-amber-400 font-medium">
                        +{partner.bonus_percentage}% Transfer Bonus
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-400 py-4 text-center">No transfer partners discovered for current accounts.</p>
          )}
        </div>

        <div className="bg-white dark:bg-[#0F172A] border border-slate-100 dark:border-slate-800 p-5 sm:p-6 rounded-2xl shadow-sm space-y-4">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-amber-500" />
            <h2 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-slate-800 dark:text-white">Your Accounts</h2>
          </div>

          <div className="divide-y divide-slate-100 dark:divide-slate-800/60">
            {cardList?.cards?.map((card, index) => (
              <div key={index} className="py-3.5 flex items-center justify-between gap-4 first:pt-0 last:pb-0 group cursor-pointer">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-amber-500/10 text-amber-600 flex items-center justify-center flex-shrink-0">
                    <Wallet size={16} />
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-xs sm:text-sm font-bold text-slate-900 dark:text-white truncate group-hover:text-amber-500 transition-colors">
                      {card.name}
                    </h4>
                    <p className="text-[11px] text-slate-400 truncate mt-0.5">
                      {card.reward_program_name}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="text-right">
                    <p className="text-sm font-extrabold text-slate-900 dark:text-white tabular-nums">
                      {Number(card.latest_points || 0).toLocaleString()}
                    </p>
                    <p className="text-[9px] text-slate-400 font-medium uppercase tracking-wider mt-0.5">
                      Points Available
                    </p>
                  </div>
                  <ChevronRight size={16} className="text-slate-300 dark:text-slate-600 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </div>
            ))}
          </div>

          <button 
            onClick={() => navigate("/dashboard-screen")}
            className="w-full h-11 rounded-xl bg-[#CAA036]/10 hover:bg-[#CAA036]/20 text-[#9A721C] dark:text-amber-400 text-xs font-bold transition-all flex items-center justify-center gap-2"
          >
            View All Accounts
            <ArrowRight size={14} />
          </button>
        </div>

        <div className="bg-emerald-500/5 dark:bg-emerald-950/20 border border-emerald-500/10 dark:border-emerald-500/20 p-4 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start sm:items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center flex-shrink-0 shadow-md shadow-emerald-500/10">
              <Sparkles size={18} />
            </div>
            <div>
              <span className="text-[9px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">Strongest Program</span>
              <h3 className="text-sm font-extrabold text-slate-900 dark:text-white mt-0.5">
                {strongestProgram?.[0] || "N/A"}
              </h3>
              <p className="text-[11px] text-slate-400 mt-0.5">Highest estimated redemption value configuration.</p>
            </div>
          </div>

          <div className="sm:text-right border-t sm:border-t-0 pt-3 sm:pt-0 border-slate-200 dark:border-slate-800">
            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Estimated Value</span>
            <p className="text-base font-extrabold text-emerald-500 tracking-tight mt-0.5 tabular-nums">
              ${cardList.total_estimated_min_value || 0} – ${cardList.total_estimated_max_value || 0}
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default PortfolioSummary;